import { inject, Injectable } from '@angular/core';
import { getFileNameExtension, isNoEmptyArray, isNullOrUndefined } from '@pineapplelab/util';
import { DbService } from '../db/db.service';
import { Project } from '../../models/project';
import { Category } from '../../models/category';
import { LanguageCategory } from '../../models/languageCategory';
import { StorageService } from '../storage/storage.service';
import { Course } from '../../models/course';
import { LanguageCategoryService } from '../language-category/language-category.service';
import { CourseService } from '../course/course.service';
import { cloneDeep } from 'lodash';
import { PublishStatusEnum } from '../../enums/publishStatus.enum';
import { UniqueIdService } from '../numeric-id/numeric-id.service';
import { DocumentData, DocumentReference } from "@angular/fire/compat/firestore";
import firebase from "firebase/compat/app";
import { RoleEnum } from '../../enums/role.enum';
import { UserService } from '../user/user.service';
import { environment } from '../../../environments/environment';
import { BehaviorSubject, Subscription } from 'rxjs';
import { User } from '../../models/user';
import { AssignedClient } from '../../models/assignedClient';
import { ApiService } from '../api/api.service';
import { Language } from '../../utils/language';

@Injectable({
	providedIn: 'root'
})
export class CategoryService {

	private db = inject(DbService);
	private languageCategoryService = inject(LanguageCategoryService);
	private courseService = inject(CourseService);
	private storageService = inject(StorageService);
	private uniqueIdService = inject(UniqueIdService);
	private userService = inject(UserService);
	private api = inject(ApiService);

	private _promotionalVideoListener: BehaviorSubject<number>;
	private _promotionalVideoSubscription: Subscription;

	async save(model: Category, languageCategory: LanguageCategory[], courses: Course[], projectId: string, isEdit: boolean, userId: string, skipTranslationFilter: boolean = false) {
		const categoryId = model.id;
		model.courses = await this.getCoursesReference(courses, projectId);

		if (isEdit) {
			model.updatedBy = userId;
			await this.languageCategoryService.deleteByCategoryId(projectId, categoryId)
		}

		const json = model.toJson();
		const [, latestLanguageCategories] = await Promise.all([
			this.db.getProyIdRef(projectId).collection(Category.PATH).doc(model.id).set(json, { merge: true }),
			this.getLatestLanguageCategories(model?.id)
		]);

		languageCategory = skipTranslationFilter
			? languageCategory
			: this.getFilteredTranslations([...languageCategory, ...latestLanguageCategories]);
		for (const language of languageCategory) {
			const doesLanguageCategoryAlreadyExist = latestLanguageCategories.find(l => l?.language?.code === language?.language?.code);
			if (doesLanguageCategoryAlreadyExist && !language?.isDefault) {
				continue;
			}
			if (isNullOrUndefined(language.id)) {
				language.id = this.db.generateFirebaseUniqueId();
			}
			await this.languageCategoryService.save(language, projectId, categoryId);
		}
	}
	// Only writes the flag: the caller's model can be stale, and writing it whole could revert
	// fields such as `status` that changed since it was loaded.
	async updateAssignedCategories(model: Category, projectId: string) {
		model.assigned = true;
		await this.db.getProyIdRef(projectId).collection(Category.PATH).doc(model.id).set({ assigned: true }, { merge: true });
	}

	// Sets the category-level quiz pass percentage override (null clears it). Read live by the
	// consumer quiz, so it propagates to every existing and new enrollment of this category.
	async updateQuizPassPercentage(categoryId: string, passPercentage: number | null, projectId: string) {
		await this.db.getProyIdRef(projectId).collection(Category.PATH).doc(categoryId).set(
			{ pass_percentage: passPercentage },
			{ merge: true }
		);
	}

	async getCoursesReference(courses: Course[], projectId: string): Promise<DocumentReference[]> {
		const courseRefs: DocumentReference[] = courses.map((course) => {
			const courseId = course.id;
			return this.db.firestore.collection(Project.PATH)
				.doc(projectId)
				.collection(Course.PATH)
				.doc(courseId);
		});
		return courseRefs;
	}

	/**
	 * Finds the category a course belongs to, by the course reference categories already store.
	 *
	 * Courses built for in-person categories are not reused elsewhere, so the first match is the
	 * course's category. This lets the course editor know whether in-person features apply even when
	 * it was opened straight from the course list, with no category in the route.
	 */
	async getByCourseId(courseId: string, projectId: string): Promise<Category | null> {
		if (isNullOrUndefined(courseId)) {
			return null;
		}

		const courseRef = this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Course.PATH)
			.doc(courseId);

		const snapshot = await this.db.getProyIdRef(projectId)
			.collection(Category.PATH)
			.where('courses', 'array-contains', courseRef)
			.where('is_deleted', '==', false)
			.get();

		if (snapshot.empty) {
			return null;
		}

		return new Category(snapshot.docs[0].data());
	}

	async saveOrRemoveImage(model: Category, projectId: string) {
		if (isNullOrUndefined(model.id)) {
			model.id = this.db.generateFirebaseUniqueId();
		}
		const commonPath = `${Project.PATH}/${projectId}/${LanguageCategory.PATH}/${model.id}`;
		if (!isNullOrUndefined(model.__fileImage)) {
			const path = `${commonPath}/image.${getFileNameExtension(model.__fileImage.name)}`;
			model.image = await this.storageService.saveOrRemoveImagesIfNecessary(model.__fileImage, path);
		} else if (model.__fileImage === null) {
			model.image = null;
		}
	}

	async getAll(projectId: string) {
		const queryCategory = this.db.getProyIdRef(projectId).collection(Category.PATH);
		const snapshot = await queryCategory.where('is_deleted', '==', false).get();

		if (snapshot.empty) {
			return null;
		}
		const categories = Promise.all(snapshot.docs.map(async category => {
			const newCategory = new Category(category.data());
			const courses = await queryCategory.doc(category.id).collection(Course.PATH).get();
			newCategory.__courses = courses.docs.map(course => new Course(course.data()));
			return newCategory;
		}));

		return categories;
	}

	async getRecentCategories(projectId: string, limit: number) {
		const queryCategory = this.db.getProyIdRef(projectId).collection(Category.PATH);
		const snapshot = await queryCategory
			.where('is_deleted', '==', false)
			.orderBy('created_at', 'desc')
			.where('status', '==', PublishStatusEnum.PUBLISHED)
			.limit(limit)
			.get();

		if (snapshot.empty) {
			return null;
		}
		const categories = await Promise.all(snapshot.docs.map(async category => {
			const newCategory = new Category(category.data());
			const coursesSnapshot = newCategory.courses.map(course => course.get());
			const coursesData = await Promise.all(coursesSnapshot);
			newCategory.__courses = coursesData.map(course => new Course(course.data()));
			return newCategory;
		}));

		return categories;
	}


	getDocumentReferenceById(categoryId: string, projectId: string): DocumentReference {
		return this.db.getProyIdRef(projectId).collection(Category.PATH).doc(categoryId);
	}

	async getById(categoryId: string, projectId: string): Promise<Category> {

		const snapshot = await this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.get();

		if (snapshot.exists) {
			return new Category(snapshot.data());
		}
		return null;
	}

	listenById(
		categoryId: string,
		projectId: string,
		next: (category: Category | null) => void,
		error?: (error: unknown) => void
	): () => void {
		return this.db.firestore
			.collection(Project.PATH)
			.doc(projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.onSnapshot(
				snapshot => next(snapshot.exists ? new Category(snapshot.data()) : null),
				error
			);
	}

	async duplicate(model: Category, projectId: string, authUser: { role: RoleEnum, id: string }) {
		const categoryCopy = cloneDeep(model);
		delete (categoryCopy as any).clients;
		categoryCopy.id = null;
		categoryCopy.createdAt = firebase.firestore.Timestamp.now();
		categoryCopy.createdBy = authUser?.id;
		categoryCopy.status = PublishStatusEnum.DRAFT;
		categoryCopy.assigned = false;
		categoryCopy.name = `${model.name} - Copy`;
		categoryCopy.nameLowercase = categoryCopy.name.toLowerCase();
		categoryCopy.id = await this.uniqueIdService.getOrCreateNextNumericId(Category.COUNTERCOLLECTION, Category.COUNTER, Category.COUNTERSTART);
		categoryCopy.createdByAdmin = authUser?.role === RoleEnum.ADMIN;
		categoryCopy.enrollments = 0;
		const languageCategories = await this.languageCategoryService.getAllById(model.id, projectId);
		languageCategories.forEach(language => (language.id = null));
		categoryCopy.translationsInherited = languageCategories.some(lc => !lc.isDefault && !lc.isDeleted);
		const courses = await this.courseService.getByCategory(model, projectId);
		categoryCopy.readyToPublish = await this.isModelReadyToPublish(categoryCopy, languageCategories, courses);
		await this.save(categoryCopy, languageCategories, courses, projectId, false, authUser?.id, true);
	}

	async isModelReadyToPublish(model: Category, languageCategory: LanguageCategory[], courses: Course[]): Promise<boolean> {
		const hasValidModel = model.isValid();
		const hasImage = model.__fileImage || model.image;
		const hasValidLanguages = languageCategory.length > 0 && languageCategory.every(language => language.isValid());
		const hasCourses = courses.length > 0;
		return hasValidModel && hasImage && hasValidLanguages && hasCourses;
	}

	async publish(model: Category, projectId: string) {
		await this.db.getProyIdRef(projectId).collection(Category.PATH).doc(model.id).update({
			status: PublishStatusEnum.PUBLISHED, ready_to_publish: false
		});
	}

	// Only writes the delete flags: the caller's model is the list row, loaded earlier, and writing it
	// whole could revert `courses` or `status` changes made since.
	async deleteById(model: Category, projectId: string, userId: string = null): Promise<void> {
		model.isDeleted = true;
		await this.db.getProyIdRef(projectId).collection(Category.PATH).doc(model.id).set({
			is_deleted: true,
			deleted_at: firebase.firestore.FieldValue.serverTimestamp(),
			deleted_by: userId,
			updated_at: firebase.firestore.FieldValue.serverTimestamp(),
		}, { merge: true });
	}

	async setCategoriesAsAssigned(categoryIds: string[], projectId: string): Promise<void> {
		if (!isNoEmptyArray(categoryIds)) {
			return;
		}

		const batch = this.db.firestore.batch();

		categoryIds.forEach(categoryId => {
			batch.set(this.getDocumentReferenceById(categoryId, projectId), { assigned: true }, { merge: true });
		});

		await batch.commit();
	}

	async linkCategoryWithUser(categoryId: string, username: string, projectId: string, userId: string): Promise<void> {
		if (!categoryId || !username || !userId) {
			return;
		}
		const userRef = this.getUserDocumentReference(userId, projectId);
		const assignedClient = new AssignedClient({
			reference: userRef,
			id: userId,
		});
		const categoryRef = this.getDocumentReferenceById(categoryId, projectId);
		await categoryRef.collection(AssignedClient.PATH).doc(userId).set(assignedClient.toJson(), { merge: true });
	}

	getUserDocumentReference(userId: string, projectId: string): DocumentReference<DocumentData> {
		return this.db.firestore.collection(Project.PATH).doc(projectId).collection(User.PATH).doc(userId);
	}

	uploadPromotionalVideo(video: File, categoryId: string, finish: (url: string) => Promise<void> | void, error: (error: any) => void): void {
		if (!video || !(video instanceof File) || !categoryId) {
			return null;
		}
		const timestamp = new Date().valueOf();
		const videoPath = `${Project.PATH}/${environment.projectId}/${LanguageCategory.PATH}/${categoryId}/promotional_video_${timestamp}.${getFileNameExtension(video?.name)}`;

		if (!this._promotionalVideoListener) {
			this._promotionalVideoListener = new BehaviorSubject<number>(0);
		}

		this._promotionalVideoSubscription = this.storageService.uploadFileWithListener({
			file: video,
			onProgress: (progress) => this._promotionalVideoListener.next(progress),
			onSuccess: async (url) => {
				await finish(url);
				this._promotionalVideoSubscription.unsubscribe();
			},
			path: videoPath,
			onError: (e) => {
				error(e);
				this._promotionalVideoSubscription.unsubscribe();
			}
		});
	}

	async uploadPromotionalThumbnail(thumbnail: File, categoryId: string): Promise<string> {
		if (!categoryId) {
			return null;
		}

		const timestamp = new Date().valueOf();
		const thumbnailPath = `${Project.PATH}/${environment.projectId}/${LanguageCategory.PATH}/${categoryId}/promotional_video_thumbnail_${timestamp}.${getFileNameExtension(thumbnail?.name)}`;

		return this.storageService.uploadFile(thumbnailPath, thumbnail);
	}

	getPromotionalVideoListener(): BehaviorSubject<number> {
		return this._promotionalVideoListener;
	}

	cancelPromotionalVideoUpload(): void {
		this.storageService.cancelUpload();
		if (!isNullOrUndefined(this._promotionalVideoSubscription)) {
			this._promotionalVideoSubscription.unsubscribe();
		}
	}

	async translate(categoryId: string, languages: Language[], includeCourses: boolean = false): Promise<string[]> {
		if (!categoryId || !languages) {
			return [];
		}

		const result = await this.api.post('/categoryLanguages/translate', {
			category_id: categoryId,
			languages,
			include_courses: includeCourses,
		}) as any;

		return result?.['languageIds'];
	}

	getLanguageCategoryListener(categoryId: string, next: (...args: any[]) => any, error: (...args: any[]) => any) {
		return this.languageCategoryService.observe(categoryId, next, error);
	}

	async getLatestLanguageCategories(categoryId: string): Promise<LanguageCategory[]> {
		if (!categoryId) {
			return [];
		}

		const result = await this.db.getProyIdRef(environment.projectId)
			.collection(Category.PATH)
			.doc(categoryId)
			.collection(LanguageCategory.PATH)
			.get();

		return result.docs.filter(l => l.exists).map(l => new LanguageCategory(l.data())).filter(l => !l?.isDeleted);
	}

	private getFilteredTranslations(languageCategories: LanguageCategory[]): LanguageCategory[] {
		if (!languageCategories || !isNoEmptyArray(languageCategories)) {
			return [];
		}

		let result: LanguageCategory[] = [];

		languageCategories.forEach(languageCategory => {
			if (languageCategory?.isDefault) {
				result.push(languageCategory);
				return;
			}
			const languageRepetitionItems = (languageCategories.filter(item => item?.language?.code === languageCategory?.language?.code) || []);
			if (languageRepetitionItems.length > 1) {
				const automaticTranslationCategory = languageRepetitionItems.find(item => ('automaticTranslation' in item) && (item?.automaticTranslation));
				if (automaticTranslationCategory) {
					result.push(automaticTranslationCategory);
					return;
				}
				const languageToPush = languageRepetitionItems.reduce((oldest, current) => {
					return (current?.createdAt?.seconds * 1000) < (oldest?.createdAt?.seconds * 1000) ? current : oldest;
				});
				if (languageToPush) {
					result.push(languageToPush);
				}
			} else {
				result.push(languageCategory);
			}
		})

		return result;
	}

	async regenerateTranslation(categoryId: string, languages: Language[], includeCourses: boolean = false): Promise<void> {
		if (!categoryId || (!languages || !languages?.length || !Array.isArray(languages))) {
			return;
		}

		await this.api.post('/categoryLanguages/translate/regenerate', {
			category_id: categoryId,
			languages,
			include_courses: includeCourses,
		});
	}

}
